import { createClient } from '@supabase/supabase-js'

const supabaseUrl = 'https://eximyshafkuyehkiotuf.supabase.co'
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4aW15c2hhZmt1eWVoa2lvdHVmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUxODc0MDEsImV4cCI6MjA3MDc2MzQwMX0.bjrI5MO_WUfnI53UaQ_m8BS8LPh2et5OrqMKmWUvYzw'

// Service role key for admin access (NEVER expose this in production frontend)
const supabaseServiceKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4aW15c2hhZmt1eWVoa2lvdHVmIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc1NTE4NzQwMSwiZXhwIjoyMDcwNzYzNDAxfQ.iGoZMXdjGdiyThkA1GH0MvJqEiIrYX18Omj7HZDKuXU'

// Create the main Supabase client (for public access)
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Create admin Supabase client with service role (for authenticated access)
export const supabaseAdmin = createClient(supabaseUrl, supabaseServiceKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true
  }
})

// Admin user credentials
const ADMIN_EMAIL = 'admin@myclass.edu.hk'
const ADMIN_PASSWORD = 'admin123' // You should change this to a secure password

// Function to ensure admin is always logged in
export const ensureAdminAuth = async () => {
  try {
    // Check if admin is already authenticated
    const { data: { session } } = await supabaseAdmin.auth.getSession()
    
    if (session && session.user.email === ADMIN_EMAIL) {
      console.log('Admin already authenticated:', session.user.email)
      return { success: true, user: session.user }
    }
    
    // If not authenticated, sign in as admin
    console.log('Authenticating as admin...')
    const { data, error } = await supabaseAdmin.auth.signInWithPassword({
      email: ADMIN_EMAIL,
      password: ADMIN_PASSWORD
    })
    
    if (error) {
      console.error('Admin authentication error:', error)
      return { success: false, error: error.message }
    }
    
    console.log('Admin authenticated successfully:', data.user.email)
    return { success: true, user: data.user }
  } catch (err) {
    console.error('Unexpected error in ensureAdminAuth:', err)
    return { success: false, error: err.message }
  }
}

// Initialize admin authentication on module load
let adminAuthInitialized = false
export const initializeAdminAuth = async () => {
  if (adminAuthInitialized) return
  
  try {
    const result = await ensureAdminAuth()
    if (result.success) {
      adminAuthInitialized = true
      console.log('Admin authentication initialized successfully')
    } else {
      console.error('Failed to initialize admin authentication:', result.error)
    }
  } catch (err) {
    console.error('Error initializing admin authentication:', err)
  }
}

// Call initialization function
initializeAdminAuth()

// Authentication functions - now use admin client
export const auth = {
  // Sign in with email and password
  signIn: async (email, password) => {
    const { data, error } = await supabaseAdmin.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  // Sign up with email and password
  signUp: async (email, password, userData) => {
    const { data, error } = await supabaseAdmin.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })
    return { data, error }
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabaseAdmin.auth.signOut()
    return { error }
  },

  // Get current user
  getCurrentUser: async () => {
    const { data: { user } } = await supabaseAdmin.auth.getUser()
    return user
  },

  // Get current session
  getCurrentSession: async () => {
    const { data: { session } } = await supabaseAdmin.auth.getSession()
    return session
  },

  // Listen to auth changes
  onAuthStateChange: (callback) => {
    return supabaseAdmin.auth.onAuthStateChange(callback)
  }
}

// User management functions
export const userManagement = {
  // Get user profile by ID
  getUserProfile: async (userId) => {
    const { data, error } = await supabaseAdmin
      .from('users')
      .select(`
        *,
        teacher_profiles (*),
        student_profiles (*),
        parent_profiles (*),
        staff_profiles (*),
        admin_profiles (*)
      `)
      .eq('id', userId)
      .single()
    return { data, error }
  },

  // Get user by username
  getUserByUsername: async (username) => {
    const { data, error } = await supabaseAdmin
      .from('users')
      .select(`
        *,
        teacher_profiles (*),
        student_profiles (*),
        parent_profiles (*),
        staff_profiles (*),
        admin_profiles (*)
      `)
      .eq('username', username)
      .single()
    return { data, error }
  },

  // Update user profile
  updateUserProfile: async (userId, updates) => {
    const { data, error } = await supabaseAdmin
      .from('users')
      .update(updates)
      .eq('id', userId)
    return { data, error }
  }
}

// Academic data functions
export const academicData = {
  // Get classes
  getClasses: async () => {
    const { data, error } = await supabaseAdmin
      .from('academic_classes')
      .select('*')
      .order('class_id')
    return { data, error }
  },

  // Get subjects
  getSubjects: async () => {
    const { data, error } = await supabaseAdmin
      .from('subjects')
      .select('*')
      .order('subject_name_zh')
    return { data, error }
  },

  // Get class memberships
  getClassMemberships: async (classId) => {
    const { data, error } = await supabaseAdmin
      .from('class_memberships')
      .select(`
        *,
        users (*),
        subjects (*)
      `)
      .eq('class_id', classId)
    return { data, error }
  },

  // Get student grades
  getStudentGrades: async (studentId, academicYear = '2024-2025', term = 'Term 1') => {
    const { data, error } = await supabaseAdmin
      .from('student_grades')
      .select(`
        *,
        subjects (*),
        academic_classes (*)
      `)
      .eq('student_id', studentId)
      .eq('academic_year', academicYear)
      .eq('term', term)
    return { data, error }
  }
}

// eNotice functions
export const eNotices = {
  // Get notices for user
  getNotices: async (userId, filters = {}) => {
    let query = supabaseAdmin
      .from('enotices')
      .select(`
        *,
        enotice_categories (*),
        enotice_priorities (*),
        user_notice_status!inner(*)
      `)
      .eq('user_notice_status.user_id', userId)
      .eq('status', 'published')

    if (filters.category) {
      query = query.eq('category_id', filters.category)
    }
    if (filters.priority) {
      query = query.eq('priority_id', filters.priority)
    }

    const { data, error } = await query.order('publish_date', { ascending: false })
    return { data, error }
  },

  // Mark notice as read
  markAsRead: async (userId, noticeId) => {
    const { data, error } = await supabaseAdmin
      .from('user_notice_status')
      .upsert({
        user_id: userId,
        notice_id: noticeId,
        read_status: true,
        read_timestamp: new Date().toISOString()
      })
    return { data, error }
  },

  // Get notice actions
  getNoticeActions: async (noticeId) => {
    const { data, error } = await supabaseAdmin
      .from('enotice_actions')
      .select(`
        *,
        enotice_action_types (*)
      `)
      .eq('notice_id', noticeId)
    return { data, error }
  }
}

// ECA functions
export const ecaData = {
  // Get ECA activities
  getActivities: async (filters = {}) => {
    let query = supabaseAdmin
      .from('eca_activities')
      .select(`
        *,
        eca_categories (*)
      `)
      .eq('status', 'active')

    if (filters.category) {
      query = query.eq('category_id', filters.category)
    }

    const { data, error } = await query.order('name')
    return { data, error }
  },

  // Get user ECA memberships
  getUserMemberships: async (userId) => {
    const { data, error } = await supabaseAdmin
      .from('eca_memberships')
      .select(`
        *,
        eca_activities (*)
      `)
      .eq('user_id', userId)
    return { data, error }
  }
}

// Homework functions
export const homeworkData = {
  // Get homework assignments for student
  getStudentHomework: async (studentId, filters = {}) => {
    let query = supabaseAdmin
      .from('homework_assignments')
      .select(`
        *,
        subjects (*),
        academic_classes (*)
      `)
      .eq('class_id', (await academicData.getClassMemberships(studentId)).data?.class_id)

    if (filters.subject) {
      query = query.eq('subject_id', filters.subject)
    }

    const { data, error } = await query.order('due_date')
    return { data, error }
  },

  // Get homework submissions
  getSubmissions: async (homeworkId) => {
    const { data, error } = await supabaseAdmin
      .from('homework_submissions')
      .select(`
        *,
        student_profiles (*)
      `)
      .eq('homework_id', homeworkId)
    return { data, error }
  }
}

// SaaS functions for RightSubjects integration
export const saasData = {
  // Get tenant credits
  getTenantCredits: async (tenantId) => {
    const { data, error } = await supabaseAdmin
      .from('tenant_credits')
      .select('*')
      .eq('tenant_id', tenantId)
      .single()
    return { data, error }
  },

  // Log usage
  logUsage: async (usageData) => {
    const { data, error } = await supabaseAdmin
      .from('usage_logs')
      .insert(usageData)
    return { data, error }
  },

  // Get RightSubjects apps
  getRightSubjectsApps: async () => {
    const { data, error } = await supabaseAdmin
      .from('right_subjects_apps')
      .select('*')
      .eq('status', 'active')
    return { data, error }
  },

  // Get all tenants
  getTenants: async () => {
    const { data, error } = await supabaseAdmin
      .from('tenants')
      .select('*')
      .eq('status', 'active')
    return { data, error }
  },

  // Get classes for a tenant
  getTenantClasses: async (tenantId) => {
    // Map tenant_id strings to actual UUID tenant IDs
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    }
    
    const actualTenantId = tenantIdMap[tenantId]
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId)
      return { data: [], error: 'Invalid tenant ID' }
    }

        const { data, error } = await supabaseAdmin
      .from('saas_classes')
      .select('*')
      .eq('tenant_id', actualTenantId)
      .order('name')
    return { data, error }
  },

  // Get students for a tenant
  getTenantStudents: async (tenantId) => {
    // Map tenant_id strings to actual UUID tenant IDs
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    }
    
    const actualTenantId = tenantIdMap[tenantId]
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId)
      return { data: [], error: 'Invalid tenant ID' }
    }

          const { data, error } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('name')
     return { data, error }
   },

  // Get teachers for a tenant
  getTenantTeachers: async (tenantId) => {
    // Map tenant_id strings to actual UUID tenant IDs
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    }
    
    const actualTenantId = tenantIdMap[tenantId]
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId)
      return { data: [], error: 'Invalid tenant ID' }
    }

    console.log(`Fetching teachers for tenant ${tenantId} -> ${actualTenantId}`);

    const { data, error } = await supabaseAdmin
      .from('saas_teachers')
      .select('*')
      .eq('tenant_id', actualTenantId)
      .order('name')
    
    console.log(`Supabase query result: ${data?.length || 0} teachers, error: ${error || 'none'}`);
    
    return { data, error }
  },

  // Get teacher data from saas_teachers table
  getTeacherData: async (tenantId = 'tn_01') => {
    try {
      // Map tenant_id strings to actual UUID tenant IDs
      const tenantIdMap = {
        'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
        'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
      }
      
      const actualTenantId = tenantIdMap[tenantId]
      if (!actualTenantId) {
        console.error('Invalid tenant ID:', tenantId)
        return { data: null, error: 'Invalid tenant ID' }
      }

      console.log(`Fetching teacher data for tenant ${tenantId} -> ${actualTenantId}`);

      // Fetch all teachers from saas_teachers table (plural, not singular)
      const { data: teachers, error: teachersError } = await supabaseAdmin
        .from('saas_teachers')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('name')
      
      if (teachersError) {
        console.error('Error fetching teachers:', teachersError)
        return { data: null, error: teachersError.message }
      }

      console.log(`Teachers fetched: ${teachers?.length || 0}`);

      // Calculate summary statistics
      const totalTeachers = teachers?.length || 0
      const uniqueSubjects = new Set()
      const seniorTeachers = teachers?.filter(teacher => {
        const experience = parseInt(teacher.experience_years) || 0
        return experience >= 5
      }).length || 0

      // Extract unique subjects
      teachers?.forEach(teacher => {
        if (teacher.subject) {
          uniqueSubjects.add(teacher.subject)
        }
        if (teacher.subjects && Array.isArray(teacher.subjects)) {
          teacher.subjects.forEach(subject => uniqueSubjects.add(subject))
        }
      })

      // Group teachers by department
      const departmentGroups = {
        '語文科': ['中文', '英文', '普通話'],
        '數理科': ['數學', '科學', '電腦'],
        '人文科': ['歷史', '地理', '公民'],
        '藝術科': ['音樂', '美術', '體育']
      }

      const departmentStats = {}
      Object.entries(departmentGroups).forEach(([dept, subjects]) => {
        const teachersInDept = teachers?.filter(teacher => {
          const teacherSubject = teacher.subject || ''
          return subjects.some(subject => teacherSubject.includes(subject))
        }).length || 0
        departmentStats[dept] = {
          name: dept,
          subjects: subjects.join('、'),
          teacherCount: teachersInDept
        }
      })

      const summaryData = {
        totalTeachers,
        uniqueSubjects: uniqueSubjects.size,
        seniorTeachers,
        teachers: teachers || [],
        departments: departmentStats
      }

      console.log('Teacher summary data:', summaryData)
      return { data: summaryData, error: null }

    } catch (err) {
      console.error('Unexpected error in getTeacherData:', err)
      return { data: null, error: err.message || 'Unexpected error' }
    }
  },

  // Get student data from saas_students table
  getStudentData: async (tenantId = 'tn_01') => {
    try {
      // Map tenant_id strings to actual UUID tenant IDs
      const tenantIdMap = {
        'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
        'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
      }
      
      const actualTenantId = tenantIdMap[tenantId]
      if (!actualTenantId) {
        console.error('Invalid tenant ID:', tenantId)
        return { data: null, error: 'Invalid tenant ID' }
      }

      console.log(`Fetching student data for tenant ${tenantId} -> ${actualTenantId}`);

      // Fetch all students from saas_students table
      const { data: students, error: studentsError } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('name')
      
      if (studentsError) {
        console.error('Error fetching students:', studentsError)
        return { data: null, error: studentsError.message }
      }

      console.log(`Students fetched: ${students?.length || 0}`);

      // Fetch classes for student count calculation
      const { data: classes, error: classesError } = await supabaseAdmin
        .from('saas_classes')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('name')
      
      if (classesError) {
        console.error('Error fetching classes:', classesError)
        return { data: null, error: classesError.message }
      }

      // Calculate summary statistics
      const totalStudents = students?.length || 0
      const totalClasses = classes?.length || 0
      
      // Calculate average attendance rate (placeholder - you can implement actual attendance calculation)
      const averageAttendanceRate = 95.2 // This should come from actual attendance data

      // Group students by grade level based on class_id patterns
      const gradeLevels = {
        '中一級': { name: '中一級', shortName: 'J1', students: [], classCount: 0 },
        '中二級': { name: '中二級', shortName: 'J2', students: [], classCount: 0 },
        '中三級': { name: '中三級', shortName: 'J3', students: [], classCount: 0 },
        '中四級': { name: '中四級', shortName: 'S4', students: [], classCount: 0 },
        '中五級': { name: '中五級', shortName: 'S5', students: [], classCount: 0 },
        '中六級': { name: '中六級', shortName: 'S6', students: [], classCount: 0 }
      }

      // Group students by grade based on class_id patterns
      students?.forEach(student => {
        const classId = student.class_id || ''
        let grade = '中一級' // default
        
        // Extract grade from class_id (e.g., "MC01X68-J1B" -> J1 -> 中一級)
        if (classId.includes('-J1')) grade = '中一級'
        else if (classId.includes('-J2')) grade = '中二級'
        else if (classId.includes('-J3')) grade = '中三級'
        else if (classId.includes('-S4')) grade = '中四級'
        else if (classId.includes('-S5')) grade = '中五級'
        else if (classId.includes('-S6')) grade = '中六級'
        
        if (gradeLevels[grade]) {
          gradeLevels[grade].students.push(student)
        }
      })

      // Calculate class counts and student distribution for each grade
      Object.values(gradeLevels).forEach(grade => {
        // Group students by class within each grade
        const classGroups = {}
        grade.students.forEach(student => {
          const className = student.class_id || '未知班別'
          if (!classGroups[className]) {
            classGroups[className] = []
          }
          classGroups[className].push(student)
        })
        
        grade.classCount = Object.keys(classGroups).length
        grade.classDetails = Object.entries(classGroups).map(([className, students]) => ({
          className,
          studentCount: students.length
        }))
      })

      // Since we don't have gender, ECA, or academic data, we'll use placeholders
      const genderDistribution = {
        male: Math.floor(totalStudents * 0.52), // Estimate 52% male
        female: Math.floor(totalStudents * 0.48)  // Estimate 48% female
      }
      
      const ecaParticipation = {
        participating: Math.floor(totalStudents * 0.75), // Estimate 75% participate
        notParticipating: Math.floor(totalStudents * 0.25)  // Estimate 25% don't
      }
      
      const academicPerformance = {
        excellent: Math.floor(totalStudents * 0.2), // Estimate 20% excellent
        needsImprovement: Math.floor(totalStudents * 0.15)  // Estimate 15% need improvement
      }

      // Get recent students (last 5 registered)
      const recentStudents = students
        ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5) || []

      const summaryData = {
        totalStudents,
        totalClasses,
        averageAttendanceRate,
        gradeLevels,
        genderDistribution,
        ecaParticipation,
        academicPerformance,
        recentStudents
      }

      console.log('Student summary data:', summaryData)
      return { data: summaryData, error: null }

    } catch (err) {
      console.error('Unexpected error in getStudentData:', err)
      return { data: null, error: err.message || 'Unexpected error' }
    }
  },

  // Get class members for a tenant
  getTenantClassMembers: async (tenantId) => {
    // Map tenant_id strings to actual UUID tenant IDs
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    }
    
    const actualTenantId = tenantIdMap[tenantId]
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId)
      return { data: [], error: 'Invalid tenant ID' }
    }

    const { data, error } = await supabaseAdmin
      .from('saas_class_members')
      .select('*')
      .eq('tenant_id', actualTenantId)
    return { data, error }
  },

  // Get teacher assignments for a tenant
  getTenantTeacherAssignments: async (tenantId) => {
    // Map tenant_id strings to actual UUID tenant IDs
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    }
    
    const actualTenantId = tenantIdMap[tenantId]
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId)
      return { data: [], error: 'Invalid tenant ID' }
    }

    console.log(`Fetching teacher assignments for tenant ${tenantId} -> ${actualTenantId}`);

    const { data, error } = await supabaseAdmin
      .from('teacher_assignments')
      .select('*')
      .eq('tenant_id', actualTenantId)
      .order('assignment_type')
    
    console.log(`Supabase teacher assignments query result: ${data?.length || 0} assignments, error: ${error || 'none'}`);
    
    return { data, error }
  },

  // Get class details with student count
  getClassWithStudentCount: async (tenantId) => {
    const { data, error } = await supabaseAdmin
      .from('saas_classes')
      .select(`
        *,
        saas_class_members!inner(tenant_id)
      `)
      .eq('tenant_id', tenantId)
    return { data, error }
  },

  // Get attendance data for classes with real student counts
  getClassAttendanceStatus: async (tenantId, date) => {
    // Map tenant_id strings to actual UUID tenant IDs
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    }
    
    const actualTenantId = tenantIdMap[tenantId]
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId)
      return { data: [], error: 'Invalid tenant ID' }
    }
    
    // Use today's date if no date provided
    const attendanceDate = date || new Date().toISOString().split('T')[0]
    
    try {
      // First get all classes for the tenant
      const { data: classes, error: classesError } = await supabaseAdmin
        .from('saas_classes')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('name')
      
      if (classesError) {
        console.error('Error fetching classes:', classesError)
        return { data: [], error: classesError }
      }
      
      if (!classes || classes.length === 0) {
        return { data: [], error: null }
      }
      
      // Then get student counts and attendance status for each class
      const attendanceData = await Promise.all(classes.map(async (cls) => {
                 const { data: students, error: studentsError } = await supabaseAdmin
           .from('saas_students')
           .select('*')
           .eq('tenant_id', actualTenantId)
           .eq('class_id', cls.class_id)
        
        if (studentsError) {
          console.error('Error fetching students for class:', cls.class_id, studentsError)
          return {
            classId: cls.class_id,
            className: cls.name,
            studentCount: 0,
            status: '未點名'
          }
        }
        
        // Check if attendance has been taken for this class today
        const { data: attendanceRecords, error: attendanceError } = await supabaseAdmin
          .from('attendance_records')
          .select('*')
          .eq('tenant_id', actualTenantId)
          .eq('class_id', cls.class_id)
          .eq('attendance_date', attendanceDate)
        
        if (attendanceError) {
          console.error('Error fetching attendance records for class:', cls.class_id, attendanceError)
        }
        
        // Calculate detailed attendance breakdown
        const attendanceBreakdown = {
          present: 0,
          absent: 0,
          late: 0,
          early_leave: 0,
          out: 0
        }
        
        if (attendanceRecords && attendanceRecords.length > 0) {
          attendanceRecords.forEach(record => {
            const status = record.attendance_status
            if (attendanceBreakdown.hasOwnProperty(status)) {
              attendanceBreakdown[status]++
            }
          })
        }
        
        // Determine attendance status based on records
        const hasAttendanceRecords = attendanceRecords && attendanceRecords.length > 0
        const expectedRecordCount = students?.length || 0
        const actualRecordCount = attendanceRecords?.length || 0
        
        let status = '未點名'
        if (hasAttendanceRecords && actualRecordCount >= expectedRecordCount) {
          status = '已點名'
        } else if (hasAttendanceRecords && actualRecordCount > 0) {
          status = '部分點名'
        }
        
        return {
          classId: cls.class_id,
          className: cls.name,
          studentCount: students?.length || 0,
          status: status,
          attendanceBreakdown: attendanceBreakdown
        }
      }))
      
      return { data: attendanceData, error: null }
    } catch (err) {
      console.error('Unexpected error in getClassAttendanceStatus:', err)
      return { data: [], error: err.message || 'Unexpected error' }
    }
  },

  // Get class members for attendance taking
  getClassMembers: async (tenantId, classId) => {
    // Map tenant_id strings to actual UUID tenant IDs
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    }
    
    const actualTenantId = tenantIdMap[tenantId]
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId)
      return { data: [], error: 'Invalid tenant ID' }
    }
    
    try {
      console.log('Fetching class members for:', { tenantId, classId, actualTenantId })
      
             // Directly query students by class_id and tenant_id
       const { data: students, error: studentsError } = await supabaseAdmin
         .from('saas_students')
         .select('*')
         .eq('tenant_id', actualTenantId)
         .eq('class_id', classId)
         .order('name')
      
      if (studentsError) {
        console.error('Error fetching students:', studentsError)
        return { data: [], error: studentsError }
      }
      
      if (!students || students.length === 0) {
        console.log('No students found for class:', classId)
        return { data: [], error: null }
      }
      
      // Format the data to match expected structure
      const formattedData = students.map(student => ({
        student_id: student.student_id,
        class_id: student.class_id,
        tenant_id: student.tenant_id,
        saas_students: student
      }))
      
      console.log('Class members fetched:', formattedData.length, 'students')
      return { data: formattedData, error: null }
    } catch (err) {
      console.error('Unexpected error in getClassMembers:', err)
      return { data: [], error: err.message || 'Unexpected error' }
    }
  },

  // Save attendance records for a class
  saveAttendanceRecords: async (tenantId, classId, attendanceData, date) => {
    // Map tenant_id strings to actual UUID tenant IDs
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    }
    
    const actualTenantId = tenantIdMap[tenantId]
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId)
      return { data: null, error: 'Invalid tenant ID' }
    }
    
    // Use today's date if no date provided
    const attendanceDate = date || new Date().toISOString().split('T')[0]
    
    try {
      console.log('Saving attendance records for:', { tenantId, classId, attendanceDate, recordCount: attendanceData.length })
      
      // Prepare attendance records for insertion
      const records = attendanceData.map(record => ({
        tenant_id: actualTenantId,
        class_id: classId,
        student_id: record.student_id,
        attendance_date: attendanceDate,
        attendance_status: record.status,
        notes: record.notes || null
      }))
      
      // Insert attendance records
      const { data, error } = await supabaseAdmin
        .from('attendance_records')
        .upsert(records, { 
          onConflict: 'tenant_id,class_id,student_id,attendance_date',
          ignoreDuplicates: false 
        })
      
      if (error) {
        console.error('Error saving attendance records:', error)
        return { data: null, error: error.message }
      }
      
      console.log('Attendance records saved successfully:', data?.length || 0, 'records')
      return { data: data, error: null }
    } catch (err) {
      console.error('Unexpected error in saveAttendanceRecords:', err)
      return { data: null, error: err.message || 'Unexpected error' }
    }
  },

  // Get existing attendance records for a class and date
  getExistingAttendanceRecords: async (tenantId, classId, date) => {
    // Map tenant_id strings to actual UUID tenant IDs
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    }
    
    const actualTenantId = tenantIdMap[tenantId]
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId)
      return { data: null, error: 'Invalid tenant ID' }
    }
    
    try {
      console.log('Fetching existing attendance records for:', { tenantId, classId, date })
      
      const { data, error } = await supabaseAdmin
        .from('attendance_records')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .eq('class_id', classId)
        .eq('attendance_date', date)
        .order('student_id')
      
      if (error) {
        console.error('Error fetching existing attendance records:', error)
        return { data: null, error: error.message }
      }
      
      console.log('Existing attendance records fetched:', data?.length || 0, 'records')
      return { data: data || [], error: null }
    } catch (err) {
      console.error('Unexpected error in getExistingAttendanceRecords:', err)
      return { data: null, error: err.message || 'Unexpected error' }
    }
  },

  // Get class data from multiple tables
  getClassData: async (tenantId = 'tn_01') => {
    try {
      // Map tenant_id strings to actual UUID tenant IDs
      const tenantIdMap = {
        'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
        'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
      }
      
      const actualTenantId = tenantIdMap[tenantId]
      if (!actualTenantId) {
        console.error('Invalid tenant ID:', tenantId)
        return { data: null, error: 'Invalid tenant ID' }
      }

      console.log(`Fetching class data for tenant ${tenantId} -> ${actualTenantId}`);

      // Fetch classroom management data (includes class masters and vice masters)
      const { data: classroomData, error: classroomError } = await supabaseAdmin
        .from('classroom_management_view')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('class_id')
      
      if (classroomError) {
        console.error('Error fetching classroom data:', classroomError)
        return { data: null, error: classroomError.message }
      }

      // Fetch class memberships
      const { data: memberships, error: membershipsError } = await supabaseAdmin
        .from('class_memberships')
        .select('*')
        .eq('academic_year', '2024-2025') // You can make this dynamic
        .order('created_at')
      
      if (membershipsError) {
        console.error('Error fetching memberships:', membershipsError)
        return { data: null, error: membershipsError.message }
      }

      // Fetch students for student count
      const { data: students, error: studentsError } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('name')
      
      if (studentsError) {
        console.error('Error fetching students:', studentsError)
        return { data: null, error: studentsError.message }
      }

      // Fetch teachers for class master count
      const { data: teachers, error: teachersError } = await supabaseAdmin
        .from('saas_teachers')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('name')
      
      if (teachersError) {
        console.error('Error fetching teachers:', teachersError)
        return { data: null, error: teachersError.message }
      }

      console.log(`Classroom data: ${classroomData?.length || 0} classes`);
      console.log(`Students: ${students?.length || 0}`);
      console.log(`Teachers: ${teachers?.length || 0}`);

      // Calculate summary statistics
      const totalClasses = classroomData?.length || 0
      const totalStudents = students?.length || 0
      const totalClassMasters = teachers?.filter(t => t.class_master || t.class_vice_master).length || 0

      // Group classes by grade level based on class_id patterns
      const gradeLevels = {
        '中一級': { name: '中一級', shortName: 'J1', classes: [], studentCount: 0, classMasters: [] },
        '中二級': { name: '中二級', shortName: 'J2', classes: [], studentCount: 0, classMasters: [] },
        '中三級': { name: '中三級', shortName: 'J3', classes: [], studentCount: 0, classMasters: [] },
        '中四級': { name: '中四級', shortName: 'S4', classes: [], studentCount: 0, classMasters: [] },
        '中五級': { name: '中五級', shortName: 'S5', classes: [], studentCount: 0, classMasters: [] },
        '中六級': { name: '中六級', shortName: 'S6', classes: [], studentCount: 0, classMasters: [] }
      }

      // Process classroom data and group by grade
      classroomData?.forEach(classroom => {
        const classId = classroom.class_id || ''
        let grade = '中一級' // default
        
        // Extract grade from class_id (e.g., "MC01X68-J1A" -> J1 -> 中一級)
        if (classId.includes('-J1')) grade = '中一級'
        else if (classId.includes('-J2')) grade = '中二級'
        else if (classId.includes('-J3')) grade = '中三級'
        else if (classId.includes('-S4')) grade = '中四級'
        else if (classId.includes('-S5')) grade = '中五級'
        else if (classId.includes('-S6')) grade = '中六級'
        
        if (gradeLevels[grade]) {
          gradeLevels[grade].classes.push(classroom)
          
          // Count students in this class
          const classStudents = students?.filter(s => s.class_id === classroom.class_id) || []
          gradeLevels[grade].studentCount += classStudents.length
          
          // Add class masters
          if (classroom.class_master_name) {
            gradeLevels[grade].classMasters.push({
              name: classroom.class_master_name,
              englishName: classroom.class_master_english_name,
              department: classroom.class_master_department,
              position: classroom.class_master_position,
              classId: classroom.class_id
            })
          }
          if (classroom.vice_class_master_name) {
            gradeLevels[grade].classMasters.push({
              name: classroom.vice_class_master_name,
              englishName: classroom.vice_class_master_english_name,
              department: classroom.vice_class_master_department,
              position: classroom.vice_class_master_position,
              classId: classroom.class_id
            })
          }
        }
      })

      // Calculate student statistics (using estimates since we don't have all fields)
      const genderDistribution = {
        male: Math.floor(totalStudents * 0.52), // Estimate 52% male
        female: Math.floor(totalStudents * 0.48)  // Estimate 48% female
      }
      
      const ecaParticipation = {
        participating: Math.floor(totalStudents * 0.75), // Estimate 75% participate
        notParticipating: Math.floor(totalStudents * 0.25)  // Estimate 25% don't
      }
      
      const academicPerformance = {
        excellent: Math.floor(totalStudents * 0.2), // Estimate 20% excellent
        needsImprovement: Math.floor(totalStudents * 0.15)  // Estimate 15% need improvement
      }

      // Get recent class registrations (last 5 classes created)
      const recentClasses = classroomData
        ?.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 5) || []

      // Get unique class masters
      const uniqueClassMasters = teachers?.filter(t => t.class_master || t.class_vice_master) || []

      const summaryData = {
        totalClasses,
        totalStudents,
        totalClassMasters: uniqueClassMasters.length,
        gradeLevels,
        genderDistribution,
        ecaParticipation,
        academicPerformance,
        recentClasses,
        uniqueClassMasters
      }

      console.log('Class summary data:', summaryData)
      return { data: summaryData, error: null }

    } catch (err) {
      console.error('Unexpected error in getClassData:', err)
      return { data: null, error: err.message || 'Unexpected error' }
    }
  },

  // Get staff data from staff_profiles table
  getStaffData: async (tenantId = 'tn_01') => {
    try {
      // Map tenant_id strings to actual UUID tenant IDs
      const tenantIdMap = {
        'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
        'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
      }
      
      const actualTenantId = tenantIdMap[tenantId]
      if (!actualTenantId) {
        console.error('Invalid tenant ID:', tenantId)
        return { data: null, error: 'Invalid tenant ID' }
      }

      console.log(`Fetching staff data for tenant ${tenantId} -> ${actualTenantId}`);

      // Fetch staff profiles with tenant filter
      const { data: staffProfiles, error: staffError } = await supabaseAdmin
        .from('staff_profiles')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('department', { ascending: true })
      
      if (staffError) {
        console.error('Error fetching staff profiles:', staffError)
        return { data: null, error: staffError.message }
      }

      console.log(`Staff profiles fetched: ${staffProfiles?.length || 0}`);
      console.log('Sample staff profiles:', staffProfiles?.slice(0, 3));

      // Process real staff data from database
      const totalStaff = staffProfiles?.length || 0
      
      // Group staff by department
      const departmentGroups = {}
      staffProfiles?.forEach(staff => {
        const dept = staff.department || 'Other'
        if (!departmentGroups[dept]) {
          departmentGroups[dept] = []
        }
        departmentGroups[dept].push(staff)
      })

      const totalDepartments = Object.keys(departmentGroups).length
      const totalPositions = totalStaff

      // Create department statistics with colors
      const departmentStats = {}
      const colors = ['#3182ce', '#38a169', '#d69e2e', '#e53e3e', '#805ad5', '#dd6b20', '#2d3748', '#c53030', '#319795', '#d53f8c']
      let colorIndex = 0
      
      Object.entries(departmentGroups).forEach(([deptName, staff]) => {
        departmentStats[deptName] = {
          name: deptName,
          count: staff.length,
          staff: staff,
          color: colors[colorIndex % colors.length]
        }
        colorIndex++
      })

      // Get recent staff (last 5 registered)
      const recentStaff = staffProfiles
        ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5) || []

      // Group staff by category for detailed views
      const teachingAssistants = staffProfiles?.filter(s => s.position?.includes('Teaching Assistant') || s.position?.includes('SEN')) || []
      const labTechnicians = staffProfiles?.filter(s => s.position?.includes('Lab Technician')) || []
      const computerTechnicians = staffProfiles?.filter(s => s.position?.includes('Computer Technician')) || []
      const supportStaff = staffProfiles?.filter(s => s.position?.includes('Social Worker') || s.position?.includes('Counsellor') || s.position?.includes('Psychologist') || s.position?.includes('Therapist')) || []
      const administrativeStaff = staffProfiles?.filter(s => s.position?.includes('Clerk') || s.position?.includes('Accountant') || s.position?.includes('Executive Officer')) || []
      const schoolLabourers = staffProfiles?.filter(s => s.position?.includes('School Labourer')) || []
      const securityStaff = staffProfiles?.filter(s => s.position?.includes('Security')) || []

      console.log('Staff filtering results:')
      console.log('Teaching Assistants:', teachingAssistants.length)
      console.log('Lab Technicians:', labTechnicians.length)
      console.log('Computer Technicians:', computerTechnicians.length)
      console.log('Support Staff:', supportStaff.length)
      console.log('Administrative Staff:', administrativeStaff.length)
      console.log('School Labourers:', schoolLabourers.length)
      console.log('Security Staff:', securityStaff.length)

      const staffByCategory = {
        teachingAssistants,
        labTechnicians,
        computerTechnicians,
        supportStaff,
        administrativeStaff,
        schoolLabourers,
        securityStaff
      }

      const summaryData = {
        totalStaff,
        totalDepartments,
        totalPositions,
        departmentStats,
        recentStaff,
        staffByCategory,
        mockData: false // Flag to indicate this is real data
      }

      console.log('Staff summary data:', summaryData)
      return { data: summaryData, error: null }

    } catch (err) {
      console.error('Unexpected error in getStaffData:', err)
      return { data: null, error: err.message || 'Unexpected error' }
    }
  },

  // Generate comprehensive mock staff data based on your requirements
  generateMockStaffData: () => {
    const mockData = {
      // Teaching Assistants
      teachingAssistants: [
        { id: 'ta_001', name: '張助教', englishName: 'Ms. Cheung', department: '中文科', position: 'Teaching Assistant', specialty: 'Chinese Language Support', phone: '+852 2345 6789', email: 'cheung.ta@school.edu.hk', status: 'Active' },
        { id: 'ta_002', name: '李助教', englishName: 'Mr. Lee', department: '英文科', position: 'Teaching Assistant', specialty: 'English Language Support', phone: '+852 2345 6790', email: 'lee.ta@school.edu.hk', status: 'Active' },
        { id: 'ta_003', name: '王助教', englishName: 'Ms. Wong', department: '數學科', position: 'Teaching Assistant', specialty: 'Mathematics Support', phone: '+852 2345 6791', email: 'wong.ta@school.edu.hk', status: 'Active' },
        { id: 'ta_004', name: '陳助教', englishName: 'Mr. Chan', department: '特殊教育', position: 'SEN Teaching Assistant', specialty: 'Special Educational Needs', phone: '+852 2345 6792', email: 'chan.sen@school.edu.hk', status: 'Active' }
      ],

      // Laboratory Technicians
      labTechnicians: [
        { id: 'lt_001', name: '劉技師', englishName: 'Mr. Lau', department: '化學實驗室', position: 'Chemistry Lab Technician', specialty: 'Chemical Safety & Equipment', phone: '+852 2345 6793', email: 'lau.chem@school.edu.hk', status: 'Active' },
        { id: 'lt_002', name: '黃技師', englishName: 'Ms. Wong', department: '物理實驗室', position: 'Physics Lab Technician', specialty: 'Physics Equipment & Safety', phone: '+852 2345 6794', email: 'wong.physics@school.edu.hk', status: 'Active' },
        { id: 'lt_003', name: '何技師', englishName: 'Mr. Ho', department: '生物實驗室', position: 'Biology Lab Technician', specialty: 'Biology Equipment & Safety', phone: '+852 2345 6795', email: 'ho.bio@school.edu.hk', status: 'Active' }
      ],

      // Computer Technicians
      computerTechnicians: [
        { id: 'ct_001', name: '吳技師', englishName: 'Mr. Ng', department: '資訊科技部', position: 'Computer Technician', specialty: 'Hardware & Network Support', phone: '+852 2345 6796', email: 'ng.it@school.edu.hk', status: 'Active' },
        { id: 'ct_002', name: '鄭技師', englishName: 'Ms. Cheng', department: '資訊科技部', position: 'Computer Technician', specialty: 'Software & System Support', phone: '+852 2345 6797', email: 'cheng.it@school.edu.hk', status: 'Active' }
      ],

      // Support Staff
      supportStaff: [
        { id: 'ss_001', name: '林社工', englishName: 'Ms. Lam', department: '學生輔導部', position: 'Social Worker', specialty: 'Student Welfare & Support', phone: '+852 2345 6798', email: 'lam.social@school.edu.hk', status: 'Active' },
        { id: 'ss_002', name: '梁輔導員', englishName: 'Mr. Leung', department: '學生輔導部', position: 'Counsellor', specialty: 'Student Counselling', phone: '+852 2345 6799', email: 'leung.counsellor@school.edu.hk', status: 'Active' },
        { id: 'ss_003', name: '謝心理學家', englishName: 'Dr. Tse', department: '學生輔導部', position: 'Educational Psychologist', specialty: 'Educational Psychology', phone: '+852 2345 6800', email: 'tse.psych@school.edu.hk', status: 'Active' },
        { id: 'ss_004', name: '馮治療師', englishName: 'Ms. Fung', department: '言語治療部', position: 'Language Therapist', specialty: 'Speech & Language Therapy', phone: '+852 2345 6801', email: 'fung.therapy@school.edu.hk', status: 'Active' }
      ],

      // Administrative Staff
      administrativeStaff: [
        { id: 'as_001', name: '朱文員', englishName: 'Ms. Chu', department: '行政部', position: 'Office Clerk', specialty: 'General Administration', phone: '+852 2345 6802', email: 'chu.clerk@school.edu.hk', status: 'Active' },
        { id: 'as_002', name: '馬會計', englishName: 'Mr. Ma', department: '財務部', position: 'Office Accountant', specialty: 'Financial Management', phone: '+852 2345 6803', email: 'ma.accountant@school.edu.hk', status: 'Active' },
        { id: 'as_003', name: '羅行政主任', englishName: 'Ms. Law', department: '行政部', position: 'Executive Officer', specialty: 'Administrative Management', phone: '+852 2345 6804', email: 'law.exec@school.edu.hk', status: 'Active' }
      ],

      // School Labourers
      schoolLabourers: [
        { id: 'sl_001', name: '鄧校工', englishName: 'Mr. Tang', department: '設施管理部', position: 'School Labourer', specialty: 'General Maintenance', phone: '+852 2345 6805', email: 'tang.labour@school.edu.hk', status: 'Active' },
        { id: 'sl_002', name: '許校工', englishName: 'Mr. Hui', department: '設施管理部', position: 'School Labourer', specialty: 'Cleaning Services', phone: '+852 2345 6806', email: 'hui.labour@school.edu.hk', status: 'Active' },
        { id: 'sl_003', name: '呂校工', englishName: 'Mr. Lui', department: '設施管理部', position: 'School Labourer', specialty: 'Grounds Maintenance', phone: '+852 2345 6807', email: 'lui.labour@school.edu.hk', status: 'Active' },
        { id: 'sl_004', name: '周校工', englishName: 'Mr. Chow', department: '設施管理部', position: 'School Labourer', specialty: 'Equipment Maintenance', phone: '+852 2345 6808', email: 'chow.labour@school.edu.hk', status: 'Active' },
        { id: 'sl_005', name: '徐校工', englishName: 'Mr. Tsui', department: '設施管理部', position: 'School Labourer', specialty: 'Security Support', phone: '+852 2345 6809', email: 'tsui.labour@school.edu.hk', status: 'Active' },
        { id: 'sl_006', name: '胡校工', englishName: 'Mr. Wu', department: '設施管理部', position: 'School Labourer', specialty: 'Transport Support', phone: '+852 2345 6810', email: 'wu.labour@school.edu.hk', status: 'Active' },
        { id: 'sl_007', name: '蕭校工', englishName: 'Mr. Siu', department: '設施管理部', position: 'School Labourer', specialty: 'Catering Support', phone: '+852 2345 6811', email: 'siu.labour@school.edu.hk', status: 'Active' },
        { id: 'sl_008', name: '葉校工', englishName: 'Mr. Yip', department: '設施管理部', position: 'School Labourer', specialty: 'Library Support', phone: '+852 2345 6812', email: 'yip.labour@school.edu.hk', status: 'Active' }
      ],

      // Security Staff
      securityStaff: [
        { id: 'sec_001', name: '方保安', englishName: 'Mr. Fong', department: '保安部', position: 'Security Officer', specialty: 'School Security', phone: '+852 2345 6813', email: 'fong.security@school.edu.hk', status: 'Active' }
      ]
    };

    // Calculate totals
    const totalStaff = mockData.teachingAssistants.length + 
                      mockData.labTechnicians.length + 
                      mockData.computerTechnicians.length + 
                      mockData.supportStaff.length + 
                      mockData.administrativeStaff.length + 
                      mockData.schoolLabourers.length + 
                      mockData.securityStaff.length;

    // Department statistics
    const departmentStats = {
      '教學支援部': {
        name: '教學支援部',
        count: mockData.teachingAssistants.length,
        staff: mockData.teachingAssistants,
        color: '#3182ce'
      },
      '實驗室技術部': {
        name: '實驗室技術部',
        count: mockData.labTechnicians.length,
        staff: mockData.labTechnicians,
        color: '#38a169'
      },
      '資訊科技部': {
        name: '資訊科技部',
        count: mockData.computerTechnicians.length,
        staff: mockData.computerTechnicians,
        color: '#d69e2e'
      },
      '學生輔導部': {
        name: '學生輔導部',
        count: mockData.supportStaff.length,
        staff: mockData.supportStaff,
        color: '#e53e3e'
      },
      '行政部': {
        name: '行政部',
        count: mockData.administrativeStaff.length,
        staff: mockData.administrativeStaff,
        color: '#805ad5'
      },
      '設施管理部': {
        name: '設施管理部',
        count: mockData.schoolLabourers.length,
        staff: mockData.schoolLabourers,
        color: '#dd6b20'
      },
      '保安部': {
        name: '保安部',
        count: mockData.securityStaff.length,
        staff: mockData.securityStaff,
        color: '#2d3748'
      }
    };

    // Recent staff (last 5)
    const allStaff = [
      ...mockData.teachingAssistants,
      ...mockData.labTechnicians,
      ...mockData.computerTechnicians,
      ...mockData.supportStaff,
      ...mockData.administrativeStaff,
      ...mockData.schoolLabourers,
      ...mockData.securityStaff
    ];

    const recentStaff = allStaff.slice(0, 5);

    return {
      totalStaff,
      totalDepartments: Object.keys(departmentStats).length,
      totalPositions: allStaff.length,
      departmentStats,
      recentStaff,
      staffByCategory: mockData
    };
  }
}

// School information functions
export const schoolData = {
  // Get school information
  getSchoolInfo: async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('school_info')
        .select('*')
        .limit(1)
      
      if (error) {
        console.error('Supabase error:', error);
        return { data: null, error: error.message || 'Database error' }
      }
      
      // Return the first record if it exists
      const result = data && data.length > 0 ? data[0] : null;
      console.log('School info result:', result);
      return { data: result, error: null }
    } catch (err) {
      console.error('Unexpected error in getSchoolInfo:', err);
      return { data: null, error: err.message || 'Unexpected error' }
    }
  },

  // Update school information (admin only)
  updateSchoolInfo: async (updates) => {
    const { data: currentData, error: fetchError } = await schoolData.getSchoolInfo()
    
    if (fetchError || !currentData) {
      return { data: null, error: fetchError || 'No school info found to update' }
    }
    
    const { data, error } = await supabaseAdmin
      .from('school_info')
      .update(updates)
      .eq('id', currentData.id)
    return { data, error }
  },

  // Get school news
  getSchoolNews: async (filters = {}) => {
    try {
      let query = supabaseAdmin
        .from('school_news')
        .select('*')
        .eq('status', 'published')

      if (filters.featured) {
        query = query.eq('featured', filters.featured)
      }
      if (filters.category) {
        query = query.eq('category', filters.category)
      }

      const { data, error } = await query.order('date', { ascending: false })
      
      if (error) {
        console.error('Supabase news error:', error);
        return { data: null, error: error.message || 'Database error' }
      }
      
      console.log('School news result:', data);
      return { data: data || [], error: null }
    } catch (err) {
      console.error('Unexpected error in getSchoolNews:', err);
      return { data: null, error: err.message || 'Unexpected error' }
    }
  },

  // Get featured news
  getFeaturedNews: async () => {
    const { data, error } = await supabaseAdmin
      .from('school_news')
      .select('*')
      .eq('status', 'published')
      .eq('featured', true)
      .order('date', { ascending: false })
      .limit(3)
    return { data, error }
  },

  // Create news article (admin/staff only)
  createNews: async (newsData) => {
    const { data, error } = await supabaseAdmin
      .from('school_news')
      .insert(newsData)
    return { data, error }
  },

  // Update news article (admin/staff only)
  updateNews: async (newsId, updates) => {
    const { data, error } = await supabaseAdmin
      .from('school_news')
      .update(updates)
      .eq('id', newsId)
    return { data, error }
  },

  // Delete news article (admin/staff only)
  deleteNews: async (newsId) => {
    const { data, error } = await supabaseAdmin
      .from('school_news')
      .delete()
      .eq('id', newsId)
    return { data, error }
  }
}

// Test function to verify Supabase connection
export const testSupabaseConnection = async () => {
  try {
    console.log('Testing Supabase connection...')
    
    // Test with service role client
    const { data, error, count } = await supabaseAdmin
      .from('saas_students_marks')
      .select('*', { count: 'exact', head: true })
      .limit(1)
    
    if (error) {
      console.error('Supabase connection test failed:', error)
      return { success: false, error: error.message }
    }
    
    console.log('Supabase connection test successful, record count:', count)
    return { success: true, data: { count } }
  } catch (err) {
    console.error('Unexpected error in connection test:', err)
    return { success: false, error: err.message }
  }
}

// Test connection after initialization
setTimeout(() => {
  testSupabaseConnection()
}, 1000)

export default supabase
